package com.kinoshazam.uiKit.components

import androidx.compose.runtime.Composable
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.action
import kinoshazam.resources.generated.resources.adventures
import kinoshazam.resources.generated.resources.app_name
import kinoshazam.resources.generated.resources.biografy
import kinoshazam.resources.generated.resources.cartoons
import kinoshazam.resources.generated.resources.comedy
import kinoshazam.resources.generated.resources.criminal
import kinoshazam.resources.generated.resources.detective
import kinoshazam.resources.generated.resources.drama
import kinoshazam.resources.generated.resources.family
import kinoshazam.resources.generated.resources.fantastic
import kinoshazam.resources.generated.resources.favorites
import kinoshazam.resources.generated.resources.fentesy
import kinoshazam.resources.generated.resources.films
import kinoshazam.resources.generated.resources.history
import kinoshazam.resources.generated.resources.melodrama
import kinoshazam.resources.generated.resources.short
import kinoshazam.resources.generated.resources.sport
import kinoshazam.resources.generated.resources.triller
import kinoshazam.resources.generated.resources.tv_shows
import kinoshazam.resources.generated.resources.uzhas
import kinoshazam.resources.generated.resources.voenniy
import kinoshazam.resources.generated.resources.voenniy_2
import kinoshazam.resources.generated.resources.western
import com.kinoshazam.LocalAppLocale
import com.kinoshazam.LocalLocalization
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.InternalResourceApi
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

@OptIn(InternalResourceApi::class)
fun String.toDrawableResource() = DrawableResource(
    "drawable:${this.substringBefore(".")}",
    setOf(
        org.jetbrains.compose.resources.ResourceItem(
            qualifiers = setOf(),
            path = "composeResources/kinoshazam.resources.generated.resources/drawable/$this",
            -1,
            -1
        ),
    )
)

fun String.toStringResource(): StringResource? {
    val key = substringBefore(".")
    return genresMap()[key] ?: genresMap()[this]
}

/**
 * Resolves a [StringResource] for the current in-app locale.
 * On web, [stringResource] is async and returns "" while loading — never treat that as final.
 * Reading [LocalLocalization] / [LocalAppLocale] forces recomposition when language changes.
 */
@Composable
fun appStringResource(resource: StringResource, fallback: String = ""): String {
    // Subscribe to in-app locale so Compose Resources re-read after language change.
    LocalLocalization.current
    LocalAppLocale.current
    val value = stringResource(resource)
    return value.ifEmpty { fallback }
}

/** Menu / genre keys like "films", "tv_shows" → localized label (never blank). */
@Composable
fun localizedResourceKey(key: String, fallback: String = key): String {
    val resource = key.toStringResource() ?: return fallback
    return appStringResource(resource, fallback = fallback)
}

/**
 * Resolves API genre keys (same as icon basename, e.g. "action" / "action.xml")
 * to the current app locale. Falls back to [name] when not a known key (e.g. TMDB labels).
 */
@Composable
fun localizedGenreName(name: String?, icon: String? = null): String {
    val key = name?.substringBefore(".") ?: icon?.substringBefore(".")
    if (!key.isNullOrBlank()) {
        key.toStringResource()?.let { return appStringResource(it, fallback = name.orEmpty()) }
    }
    return name.orEmpty()
}

private fun genresMap(): Map<String, StringResource> {
    return mapOf(
        "action" to Res.string.action,
        "biografy" to Res.string.biografy,
        "western" to Res.string.western,
        "voenniy" to Res.string.voenniy,
        "detective" to Res.string.detective,
        "voenniy_2" to Res.string.voenniy_2,
        "melodrama" to Res.string.melodrama,
        "drama" to Res.string.drama,
        "history" to Res.string.history,
        "comedy" to Res.string.comedy,
        "criminal" to Res.string.criminal,
        "adventures" to Res.string.adventures,
        "family" to Res.string.family,
        "triller" to Res.string.triller,
        "uzhas" to Res.string.uzhas,
        "fantastic" to Res.string.fantastic,
        "fentesy" to Res.string.fentesy,
        "short" to Res.string.short,
        "sport" to Res.string.sport,
        "appName" to Res.string.app_name,
        "films" to Res.string.films,
        "tv_shows" to Res.string.tv_shows,
        "cartoons" to Res.string.cartoons,
        "favorites" to Res.string.favorites,
    )
}
